import React, {useEffect} from 'react';
import { useQuery } from '@apollo/client';
import {
    SUMMARY_STATISTICS_QUERY,
    SummaryStatisticsQuery,
    SummaryStatisticsQueryVariables
} from 'data/ForecastingResults';
import { useForecastingState } from './ForecastingContextProvider';
import {useMap} from 'react-leaflet';
import {LatLng, LatLngBounds} from 'leaflet';
import {formatNumber, displayKey} from 'leaflet/util/SummaryFormatting';
import CSS from 'csstype';
import ForecastingCard from './ForecastingCard';

export default function SummaryStatistics(
    {
        setFlyDest
    }: {
        setFlyDest: React.Dispatch<React.SetStateAction<LatLngBounds | null>>
    }) {
    const { forecastingId, forecastingState } =
        useForecastingState();
    const map = useMap();

    const { loading, error, data } = useQuery<
        SummaryStatisticsQuery,
        SummaryStatisticsQueryVariables
    >(SUMMARY_STATISTICS_QUERY, {
        variables: {
            forecastingId: forecastingId,
            url: window.location.href
        },
        skip: forecastingState !== 'loaded',
    });

    const minLat = data?.forecastingResults.data?.summary?.minLat;
    const minLon = data?.forecastingResults.data?.summary?.minLon;
    const maxLat = data?.forecastingResults.data?.summary?.maxLat;
    const maxLon = data?.forecastingResults.data?.summary?.maxLon;

    useEffect(() => {
        if (minLat == null || minLon == null || maxLat == null || maxLon == null) {
            return;
        }

        const boundary = new LatLngBounds(
            new LatLng(minLat, minLon),
            new LatLng(maxLat, maxLon)
        );
        const zoom = map.getBoundsZoom(boundary);
        const center = boundary.getCenter()
        setFlyDest(boundary)
        map.flyTo(center, zoom, {easeLinearity: 1, animate: true});
    }, [minLat,
        minLon,
        maxLat,
        maxLon]);
    

    if (forecastingState !== 'loaded') return null;

    const numberOfScreens =
        data?.forecastingResults.data?.summary?.numberOfScreens as number;
    const potentialSpend = data?.forecastingResults.data?.summary?.potentialSpend as number;
    const averageCpm = data?.forecastingResults.data?.summary?.averageCpm as number;
    const numberOfImpressions =
        data?.forecastingResults.data?.summary?.numberOfImpressions as number;
    const numberOfAudienceImpressions =
        data?.forecastingResults.data?.summary?.numberOfAudienceImpressions as number;
    
    const summaryData = data?.forecastingResults.data?.summary!;
    
    return (
        <div className="leaflet-top leaflet-right" style={{margin: '16px'}}>
            <ForecastingCard>
                <div className="w-fit">
                    <span style={summaryStatisticsTitle}>
                        Forecasting summary
                    </span>
                </div>
                {loading ? (
                    <>Loading...</>
                ) : error ? (
                    <>Error</>
                ) : (
                    <>
                        <div>
                            {(['numberOfScreens', 'potentialSpend', 'averageCpm', 'numberOfImpressions', 'numberOfAudienceImpressions'] as const).map(it => {
                                return <div key={it} className="mt-2 space-y-1">
                                    <span className="block text-sm font-medium text-gray-700">
                                        {displayKey(it)}: {formatNumber(summaryData, it)}
                                    </span>
                                </div>;
                            })}
                        </div>
                    </>
                )}
            </ForecastingCard>
        </div>
    );
}

const summaryStatisticsTitle: CSS.Properties = {
    display: 'block',
    fontWeight: '600',
    fontSize: '16px',
    color: 'text-gray-600',
}
